/**
 * Site configuration utilities
 * Provides consistent access to site URLs and other configuration
 */

const PRODUCTION_URL = 'https://app.nexusbookai.com';

/**
 * Get the base site URL
 * Priority: VITE_SITE_URL env var → window.location.origin (only if it's a real http(s) origin)
 * On native iOS (Capacitor), window.location.origin is capacitor://localhost which is
 * not a valid public URL, so we fall back to the production URL.
 */
export const getSiteUrl = (): string => {
  // Dev server: always use the live origin (localhost) so flows that round-trip
  // through external redirects (Stripe checkout, OAuth) come back to the dev
  // session instead of production. VITE_SITE_URL would otherwise send test
  // signups to app.nexusbookai.com where no session exists.
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (origin && origin.startsWith('http')) {
      return origin;
    }
  }

  const envSiteUrl = import.meta.env.VITE_SITE_URL;
  if (envSiteUrl) {
    return envSiteUrl;
  }

  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    // Only use the browser origin if it's a real http(s) URL (not capacitor://, ionic://, etc.)
    if (origin && origin.startsWith('http')) {
      return origin;
    }
  }

  return PRODUCTION_URL;
};

/**
 * Get a full URL for a specific path
 * @param path - The path to append to the site URL
 */
export const getFullUrl = (path: string): string => {
  const baseUrl = getSiteUrl();
  
  // Ensure path starts with a slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Configuration for authentication redirects
 */
export const authConfig = {
  /**
   * URL to redirect to after password reset
   */
  passwordResetRedirectUrl: getFullUrl('/reset-password/update'),
  
  /**
   * URL to redirect to after email verification
   */
  emailVerificationRedirectUrl: getFullUrl('/login'),
  
  /**
   * URL to redirect to after sign in
   */
  signInRedirectUrl: getFullUrl('/')
}; 